import React, { useEffect, useRef, useState } from 'react';

import YoutubeIFrame from '@/common/components/YoutubeIFrame';

import ProgressUtilityService from '@/services/course/progress-utility-service';
import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import { CourseId, SubunitId, UnitId, BlockingContent } from 'course-types';
import Request from '@/services/util/request-service';
import { useAppSelector } from '@/lib/hooks';
import LockedContent from '@/common/components/LockedContent';

const propTypes = {};

interface Props {
  videoURL: string;
  videoId: string;
  subunitId: string | undefined;
  unitId: string | undefined;
  courseId: string;
  activityDescription: string;
}

function VideoHandler({
  videoURL,
  videoId,
  subunitId,
  unitId,
  courseId,
  activityDescription,
}: Props) {
  const [videoHandlerStyles, setVideoHandlerStyles] = useState<React.CSSProperties>({});
  const [locked, setLocked] = useState<boolean>(false);
  const [blockingContent, setBlockingContent] = useState<BlockingContent>({} as BlockingContent);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkVideoAccess = async () => {
      try {
        const response = await Request.post('progress/content/access', {
          contentId: videoId,
          contentType: 'video',
          courseId: courseId,
        });

        if (!response) {
          throw new Error('Failed to check video access');
        }

        const resBody = await response.json();
        if (resBody.isLocked && resBody.blockingContent) {
          setLocked(true);
          setBlockingContent(resBody.blockingContent || ({} as BlockingContent));
          return;
        }

        setLocked(false);
        setBlockingContent({} as BlockingContent);
      } catch (error) {
        console.error('Error checking video access:', error);
        // If API call fails, assume video is accessible
        setLocked(false);
      }
    };

    if (
      videoId &&
      ((subunitId && subunitId != '') || (unitId && unitId != '') || unitId) &&
      courseId
    ) {
      checkVideoAccess();
      onOpen();
    }
  }, [videoId, unitId, subunitId, courseId]);

  // Handles resizing of window / component bounds
  useEffect(() => {
    calculateResize();
    window.addEventListener('resize', calculateResize);

    return () => {
      window.removeEventListener('resize', calculateResize);
    };
  }, [containerRef]);

  const calculateResize = () => {
    if (containerRef.current) {
      const maxH = containerRef.current.offsetHeight;
      // Reserve space for up to 3 lines of description (1.5rem line-height x 3) + gap-6 (1.5rem)
      const hasDescription = !!activityDescription && activityDescription.trim().length > 0;
      const LINE_HEIGHT_PX = 24; // leading-6
      const GAP_PX = 24; // gap-6 between video and description
      const reservedPx = hasDescription ? LINE_HEIGHT_PX * 3 + GAP_PX : 0;

      // Calculate comparable dimension units to determine the constraining dimension
      const hUnit = Math.max(0, maxH - reservedPx) / 9.0;
      const wUnit = containerRef.current.offsetWidth / 16.0;

      // Scale based on the constraining dimension
      const newWidth = hUnit < wUnit ? Math.floor(hUnit * 16.0) : Math.floor(wUnit * 16.0);
      setVideoHandlerStyles({ width: `${newWidth}px` });
    }
  };

  // Observe container size changes to recalc without window resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      calculateResize();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, [containerRef, activityDescription]);

  // Recalculate when description content toggles or changes
  useEffect(() => {
    calculateResize();
  }, [activityDescription]);

  const onOpen = async () => {
    // TODO:: re-pull progress if cookie not set / cleared
    //        Edge case where current set method may overwrite database progress

    const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
      courseId,
      unitId,
      subunitId,
    );

    // Define progress if undefined
    if (!activityData.video[videoId]) {
      activityData.video[videoId] = {};
    }

    // Set openDate in DB if undefined
    if (!activityData.video[videoId]?.openDate) {
      activityData.video[videoId].openDate = new Date();

      await ProgressUtilityService.updateUserCourseProgressDatabase(
        courseId as CourseId,
        activityData,
        unitId as UnitId,
        subunitId as SubunitId,
      );
    }

    // Set progress cookie with new data
    ActivityUtilityService.updateActivityProgressLocal(
      progressCookie,
      activityData,
      courseId,
      unitId,
      subunitId,
    );
  };

  if (locked) {
    return <LockedContent blockingContent={blockingContent} courseId={courseId} />;
  }

  return (
    <div ref={containerRef} className='w-full h-full'>
      <div
        className={`mx-auto flex flex-col items-center justify-center gap-6 ${!videoHandlerStyles.width && 'h-[0px]'}`}
        style={videoHandlerStyles}
      >
        {/* iframe Container */}
        <div className='w-full flex-grow'>
          <YoutubeIFrame link={videoURL || ''} />
        </div>

        {/* Description */}
        {activityDescription && activityDescription.trim().length > 0 && (
          <div className='w-full max-h-[4.5rem] overflow-y-auto break-words whitespace-pre-wrap text-base leading-6'>
            {activityDescription}
          </div>
        )}
      </div>
    </div>
  );
}

VideoHandler.propTypes = propTypes;
export default VideoHandler;
